/**
 * Speaker-gate placeholder detection — the SQUARE-BRACKET convention.
 *
 * Speaker email templates mark unfilled spots as literal bracket tokens
 * ("[TBD]", "[Registration link]", "[Hotel booking link]", "[Prep call
 * notes]"). The server's approve guard rejects a send containing any of them
 * (`_LITERAL_PLACEHOLDER_RE` in pixel/workflows/speaker/email_gates.py) —
 * this mirror powers the LIVE gate-review affordance (disable Approve & Send
 * + jump-to-first) so reviewers never trip the 409 backstop.
 *
 * Distinct from lib/findPlaceholders.ts (the sponsor `{...}` convention):
 * sponsor gates keep their behavior unchanged.
 */

/** Mirrors the server's `_LITERAL_PLACEHOLDER_RE` — keep the two in sync. */
const SPEAKER_PLACEHOLDER_RE = /\[(TBD|Registration link|Hotel booking link|Prep call notes)/gi;

export interface SpeakerPlaceholderMatch {
  /** The matched token text, e.g. "[TBD" (as the server matches it). */
  token: string;
  /** Character offset into the searched string (for jump-to-first). */
  index: number;
}

/** All speaker placeholder matches in a string, in document order. Pure. */
export function findSpeakerPlaceholders(text: string): SpeakerPlaceholderMatch[] {
  const out: SpeakerPlaceholderMatch[] = [];
  for (const m of text.matchAll(SPEAKER_PLACEHOLDER_RE)) {
    out.push({ token: m[0], index: m.index ?? 0 });
  }
  return out;
}

/** Unique placeholder labels (normalized) across subject + body, for the confirm modal. */
export function uniqueSpeakerPlaceholders(subject: string, body: string): string[] {
  const seen = new Set<string>();
  for (const m of [...findSpeakerPlaceholders(subject), ...findSpeakerPlaceholders(body)]) {
    // Normalize case so "[tbd" and "[TBD" collapse; display the canonical token.
    seen.add(m.token.replace(/^\[/, '').toUpperCase() === 'TBD' ? 'TBD' : m.token.slice(1));
  }
  return [...seen];
}
